import React, { useEffect, useState, useCallback } from "react";
import { AtSign, Send, Inbox, Loader2, Wand2, RefreshCw, Lock, Mail } from "lucide-react";
import { mailStatus, mailSend, mailInbox, mailSent, helperGenerate } from "../lib/api";
import { toast } from "sonner";

export default function NexusMail({ userId }) {
  const [status, setStatus] = useState(null);
  const [tab, setTab] = useState("compose");
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [drafting, setDrafting] = useState(false);
  const [sending, setSending] = useState(false);
  const [inbox, setInbox] = useState([]);
  const [sent, setSent] = useState([]);
  const [open, setOpen] = useState(null);

  const loadStatus = useCallback(async () => { try { setStatus(await mailStatus(userId)); } catch (e) {} }, [userId]);
  const loadInbox = useCallback(async () => { try { const r = await mailInbox(userId); setInbox(r.messages || []); } catch (e) {} }, [userId]);
  const loadSent = useCallback(async () => { try { const r = await mailSent(userId); setSent(r.messages || []); } catch (e) {} }, [userId]);
  useEffect(() => { loadStatus(); loadInbox(); loadSent(); }, [loadStatus, loadInbox, loadSent]);

  const draft = async () => {
    if (!subject.trim() && !body.trim() && !to.trim()) { toast.message("Tell Nexus what the email is about first (type a note in the body)."); return; }
    setDrafting(true);
    try {
      const instr = `Write an email${to ? ` to ${to}` : ""}. ${subject ? `Topic/subject: ${subject}. ` : ""}${body ? `Notes/points: ${body}` : ""}`;
      const r = await helperGenerate(userId, { task: "email", tone: "Professional", instructions: instr });
      const text = r.text || "";
      const m = text.match(/^\s*subject:\s*(.+)$/im);
      if (m) { setSubject(m[1].trim()); setBody(text.replace(/^\s*subject:\s*.+$/im, "").trim()); }
      else setBody(text.trim());
      toast.success("Nexus drafted it — review & send");
    } catch (e) { toast.error("Could not draft"); }
    finally { setDrafting(false); }
  };

  const send = async () => {
    const recips = to.split(/[,\s]+/).map((x) => x.trim()).filter(Boolean);
    if (!recips.length) { toast.message("Add a recipient."); return; }
    if (!body.trim()) { toast.message("The email is empty."); return; }
    setSending(true);
    try {
      await mailSend(userId, { to: recips, subject, text: body });
      toast.success("Sent from " + (status?.from || "Nexus"));
      setTo(""); setSubject(""); setBody(""); loadSent();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send"); }
    finally { setSending(false); }
  };

  const inp = "w-full bg-[#0a1526] text-cyan-50 border border-[#00F0FF33] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#00F0FF]";
  const tabBtn = (id, label, Icon) => (
    <button onClick={() => { setTab(id); if (id === "inbox") loadInbox(); if (id === "sent") loadSent(); }} data-testid={`mail-tab-${id}`}
      className={`px-3 py-1.5 rounded-lg nx-mono text-[11px] font-bold flex items-center gap-1.5 ${tab === id ? "text-[#030712] bg-[#00F0FF]" : "text-cyan-200/60 border border-cyan-200/25"}`}><Icon className="w-3.5 h-3.5" /> {label}</button>
  );

  const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch (e) { return ""; } };

  return (
    <div className="lg:col-span-2 nx-glass rounded-2xl p-4 space-y-4" data-testid="nexus-mail-panel">
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "radial-gradient(circle at 40% 30%, #00F0FF, #061018 75%)", boxShadow: "0 0 20px #00F0FF66" }}><AtSign className="w-5 h-5 text-white" /></div>
        <div>
          <h2 className="nx-orbitron font-black tracking-[0.2em] text-[#00F0FF] text-base leading-none">NEXUS MAIL</h2>
          <p className="text-cyan-100/45 text-[10px] nx-mono mt-0.5">{status?.from || "nexus@avtechai.org"} · Nexus emails on your behalf</p>
        </div>
        <button onClick={() => { loadStatus(); loadInbox(); loadSent(); }} data-testid="mail-refresh" className="ml-auto text-cyan-200/50 hover:text-[#00F0FF]"><RefreshCw className="w-4 h-4" /></button>
      </div>

      {status && !status.configured && (
        <div data-testid="mail-not-configured" className="rounded-xl bg-amber-500/10 border border-amber-400/30 p-3 text-amber-200/90 text-[12px] nx-mono flex items-start gap-2">
          <Lock className="w-4 h-4 mt-0.5 shrink-0" />
          <span>Nexus's email isn't connected yet. Create a free <b>resend.com</b> account, get an <b>API key</b>, and give it to me — then verify <b>avtechai.org</b> in Resend so Nexus can send from <b>{status.from}</b>. I'll switch it on the moment you share the key.</span>
        </div>
      )}

      <div className="flex gap-2">{tabBtn("compose", "Compose", Send)}{tabBtn("inbox", `Inbox${inbox.length ? ` (${inbox.length})` : ""}`, Inbox)}{tabBtn("sent", "Sent", Mail)}</div>

      {tab === "compose" && (
        <div className="space-y-2" data-testid="mail-compose">
          <input className={inp} value={to} onChange={(e) => setTo(e.target.value)} placeholder="To (comma-separated)" data-testid="mail-to" />
          <input className={inp} value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" data-testid="mail-subject" />
          <textarea className={`${inp} leading-relaxed`} rows={9} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Write the email, or jot a few notes and let Nexus draft it…" data-testid="mail-body" />
          <div className="flex gap-2">
            <button onClick={draft} disabled={drafting} data-testid="mail-draft" className="px-4 py-2.5 rounded-xl nx-mono text-xs font-bold text-[#00F0FF] border border-[#00F0FF44] hover:bg-[#00F0FF11] disabled:opacity-40 flex items-center gap-2">{drafting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} Draft with Nexus</button>
            <button onClick={send} disabled={sending} data-testid="mail-send" className="flex-1 py-2.5 rounded-xl nx-orbitron font-bold text-[#030712] bg-[#00F0FF] disabled:opacity-40 flex items-center justify-center gap-2">{sending ? <><Loader2 className="w-5 h-5 animate-spin" /> Sending…</> : <><Send className="w-5 h-5" /> Send as Nexus</>}</button>
          </div>
        </div>
      )}

      {tab === "inbox" && (
        <div className="space-y-2" data-testid="mail-inbox">
          {inbox.length === 0 ? <p className="text-cyan-100/30 text-sm nx-mono text-center py-8">No emails received yet. Replies to {status?.from} land here.</p>
            : inbox.map((m, i) => (
              <div key={i} data-testid="mail-inbox-item" className="rounded-xl bg-[#0a1526] border border-[#00F0FF22] p-3">
                <button onClick={() => setOpen(open === i ? null : i)} className="w-full text-left">
                  <p className="text-cyan-50 text-sm font-semibold truncate">{m.subject || "(no subject)"}</p>
                  <p className="text-cyan-100/40 text-[11px] nx-mono truncate">{m.from} · {fmt(m.created_at)}</p>
                </button>
                {open === i && <p className="mt-2 text-cyan-100/70 text-[13px] whitespace-pre-wrap border-t border-[#00F0FF22] pt-2">{m.text || "(no text body)"}</p>}
              </div>
            ))}
        </div>
      )}

      {tab === "sent" && (
        <div className="space-y-2" data-testid="mail-sent">
          {sent.length === 0 ? <p className="text-cyan-100/30 text-sm nx-mono text-center py-8">Nothing sent yet.</p>
            : sent.map((m, i) => (
              <div key={i} data-testid="mail-sent-item" className="rounded-xl bg-[#0a1526] border border-[#00F0FF22] p-3">
                <p className="text-cyan-50 text-sm font-semibold truncate">{m.subject || "(no subject)"}</p>
                <p className="text-cyan-100/40 text-[11px] nx-mono truncate">To {(m.to || []).join(", ")} · {fmt(m.created_at)}</p>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}
